/**
 * DRAFT-TRUTH-1 B — the server's start-date plan (POST
 * work/projects/:id/start-date { dryRun: true }) and the preview copy the
 * Start / Re-plan dialog shows for it.
 */
export interface StartDatePlan {
  anchorSource: 'PROJECT_START_DATE' | 'EARLIEST_TASK_START' | 'NONE' | 'WORK_UNDER_WAY';
  /** TR-2026-10-22-20: work is under way — nothing moves. */
  workUnderWay: boolean;
  oldAnchor: string | null;
  /** null only when work is under way and no start is known. */
  newAnchor: string | null;
  shiftDays: number | null;
  tasksMoved: number;
  phasesMoved: number;
  constraintConflicts: number;
  projectEndDate: { from: string; to: string } | null;
  timeZone: string;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** The preview sentence(s): "N tasks move by X days", phases, planned end, constraint conflicts. */
export function previewLines(plan: StartDatePlan): string[] {
  if (plan.workUnderWay) {
    return ['Work is under way; dates stay as they are.'];
  }
  if (plan.shiftDays === null) {
    return [
      `No planned dates to move. The start date will be set to ${plan.newAnchor}.`,
    ];
  }
  if (plan.shiftDays === 0) {
    return ['Nothing moves: this is already the planned start.'];
  }
  const days = Math.abs(plan.shiftDays);
  const direction = plan.shiftDays > 0 ? 'later' : 'earlier';
  const lines = [
    `${plural(plan.tasksMoved, 'task moves', 'tasks move')} by ${plural(days, 'day', 'days')} ${direction}.`,
  ];
  if (plan.phasesMoved > 0) {
    lines.push(`${plural(plan.phasesMoved, 'phase moves', 'phases move')} by the same amount.`);
  }
  if (plan.projectEndDate) {
    lines.push(`Planned end: ${plan.projectEndDate.from} → ${plan.projectEndDate.to}.`);
  }
  if (plan.constraintConflicts > 0) {
    lines.push(
      `${plural(plan.constraintConflicts, 'task has', 'tasks have')} a constraint date that now conflicts. Constraints are not changed.`,
    );
  }
  return lines;
}
